import { useQuery } from '@apollo/react-hooks';
import DynamicTable from '@atlaskit/dynamic-table';
import { format } from 'date-fns';
import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import { NOTIFICATIONS_QUERY } from '../../graphql/queries';
import { getTextColor } from '../common/helpers';
import Loading from '../common/Loading';

const Empty = styled.div`
    h3,
    p {
        margin-bottom: 14px;
    }
    img {
        width: 160px;
        margin-top: 14px;
    }
`;

const TypeLabel = ({ type }) => (
    <Fragment>
        {type && (
            <p
                style={{
                    backgroundColor: type.color,
                    color: getTextColor(type.color),
                    borderRadius: '3px',
                    padding: '5px 10px',
                    width: 'max-content',
                    lineHeight: '16px',
                }}
            >
                {type.name}
            </p>
        )}
        {!type && <p />}
    </Fragment>
);

function getTableHead() {
    return {
        cells: [
            {
                key: 'title',
                content: 'Title',
                isSortable: true,
            },
            {
                key: 'type',
                content: 'Type',
                width: 10,
            },
            {
                key: 'created_at',
                content: 'Created at',
                width: 10,
            },
            {
                key: 'published_at',
                content: 'Published at',
                width: 10,
            },
            {
                key: 'analytics',
                content: '',
                width: 10,
            },
            {
                key: 'details',
                content: '',
                width: 15,
            },
        ],
    };
}

function getTableRows({ notifications, openDetails }) {
    const rows = [];
    notifications.forEach((notification) => {
        const { title, notificationType, createdAt, publishedAt } = notification;
        rows.push({
            cells: [
                {
                    key: 'title',
                    name: 'Title',
                    content: <p>{title}</p>,
                },
                {
                    key: 'type',
                    name: 'Type',
                    content: <TypeLabel type={notificationType}></TypeLabel>,
                },
                {
                    key: 'created_at',
                    name: 'Created at',
                    content: <p>{createdAt && format(new Date(createdAt), 'MMM d, yyyy')}</p>,
                },
                {
                    key: 'published_at',
                    name: 'Published on',
                    content: <p>{publishedAt ? format(new Date(publishedAt), 'MMM d, yyyy') : 'Not published'}</p>,
                },
                {
                    name: 'analytics',
                    content: (
                        <p
                            style={{
                                cursor: 'pointer',
                                color: '#0052CC',
                                WebkitLineClamp: 1,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            Analytics &gt;
                        </p>
                    ),
                },
                {
                    name: 'details',
                    content: (
                        <p
                            style={{
                                cursor: 'pointer',
                                color: '#0052CC',
                                WebkitLineClamp: 1,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                            }}
                            onClick={() => {
                                openDetails(notification);
                            }}
                        >
                            Notification details &gt;
                        </p>
                    ),
                },
            ],
        });
    });
    return rows;
}

function EmptyView() {
    const message = 'Create a new notification';
    const subMessage =
        'Let your users know what you’re working on! Create a notification and then edit its details and publish!';
    return (
        <Empty>
            <img alt="no results" src="/no-notifications.svg" />
            <h3>{message}</h3>
            <p>{subMessage}</p>
        </Empty>
    );
}

function NotificationsTable({ widgetId, openDetails }) {
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState({ key: 'title', order: 'ASC' });

    const AVAILABLE_HEIGHT = window.innerHeight - 280;
    const ROW_HEIGHT = 61;

    const { data: notificationsData, loading: notificationsLoading } = useQuery(NOTIFICATIONS_QUERY, {
        variables: { widgetId },
    });
    if (notificationsLoading) {
        return <Loading />;
    }
    return (
        <DynamicTable
            emptyView={<EmptyView />}
            head={getTableHead()}
            rows={getTableRows({
                notifications: notificationsData.notifications,
                openDetails,
            })}
            rowsPerPage={Math.floor(AVAILABLE_HEIGHT / ROW_HEIGHT)}
            isLoading={false}
            defaultSortKey={sort.key}
            defaultSortOrder={sort.order}
            isFixedSize
            page={page}
            onSetPage={(newPage) => setPage(newPage)}
            onSort={(data) => setSort({ key: data.key, order: data.sortOrder })}
        />
    );
}

export default NotificationsTable;
